/**
 * Plugin Download Configuration
 *
 * Contains URLs and metadata for desktop plugin installers.
 * URLs usually point to Vercel Blob storage or public release assets.
 */

import { parsePluginVersion } from '@/lib/host-bridge/plugin-version';
import type { TranslationKey } from '@/lib/i18n/utils';
import { INTEGRATION_SUPPORT } from '@/lib/integrations/support';

const PLUGIN_DOWNLOAD_BASE_URL =
  'https://9ox6fqhlzitnx1nx.public.blob.vercel-storage.com';

/**
 * Version every installer falls back to when its NEXT_PUBLIC_*_VERSION is
 * unset. This is what the update manifest publishes as `latestVersion`, so
 * it must name a release whose payloads are actually armed in blob storage.
 *
 * Deliberately NOT the recommended-version constants: those drive the in-app
 * compat banner and are held one release back on purpose. Wiring downloads to
 * them made lowering the banner threshold silently lower `latestVersion` too,
 * which is what the agents gate on.
 */
const DEFAULT_INSTALLER_VERSION = '2.0.1';

function getPluginDownloadVersion(): string {
  const configured =
    process.env.NEXT_PUBLIC_REVIT_PLUGIN_DOWNLOAD_VERSION?.trim();
  if (configured && parsePluginVersion(configured)) {
    return configured;
  }

  return DEFAULT_INSTALLER_VERSION;
}

const pluginDownloadVersion = getPluginDownloadVersion();
const pluginDownloadFilename =
  process.env.NEXT_PUBLIC_REVIT_PLUGIN_DOWNLOAD_FILENAME?.trim() ||
  `Arkyv.RevitPlugin.${pluginDownloadVersion}.exe`;
const pluginDownloadUrl =
  process.env.NEXT_PUBLIC_REVIT_PLUGIN_DOWNLOAD_URL?.trim() ||
  `${PLUGIN_DOWNLOAD_BASE_URL}/${pluginDownloadFilename}`;

const archicadWindowsInstallerVersion =
  process.env.NEXT_PUBLIC_ARCHICAD_WINDOWS_INSTALLER_VERSION?.trim() ||
  DEFAULT_INSTALLER_VERSION;
const archicadWindowsInstallerFilename =
  process.env.NEXT_PUBLIC_ARCHICAD_WINDOWS_INSTALLER_FILENAME?.trim() ||
  `ArkyvArchicadPlugin-27-28-29-${archicadWindowsInstallerVersion}.msi`;
const archicadWindowsInstallerUrl =
  process.env.NEXT_PUBLIC_ARCHICAD_WINDOWS_INSTALLER_DOWNLOAD_URL?.trim() ||
  `${PLUGIN_DOWNLOAD_BASE_URL}/${archicadWindowsInstallerFilename}`;

const archicadMacos28InstallerVersion =
  process.env.NEXT_PUBLIC_ARCHICAD_MACOS_28_INSTALLER_VERSION?.trim() ||
  DEFAULT_INSTALLER_VERSION;
const archicadMacos28InstallerFilename =
  process.env.NEXT_PUBLIC_ARCHICAD_MACOS_28_INSTALLER_FILENAME?.trim() ||
  `ArkyvArchicadPlugin-28-${archicadMacos28InstallerVersion}.pkg`;
const archicadMacos28InstallerUrl =
  process.env.NEXT_PUBLIC_ARCHICAD_MACOS_28_INSTALLER_DOWNLOAD_URL?.trim() ||
  `${PLUGIN_DOWNLOAD_BASE_URL}/${archicadMacos28InstallerFilename}`;

const archicadMacos29InstallerVersion =
  process.env.NEXT_PUBLIC_ARCHICAD_MACOS_29_INSTALLER_VERSION?.trim() ||
  DEFAULT_INSTALLER_VERSION;
const archicadMacos29InstallerFilename =
  process.env.NEXT_PUBLIC_ARCHICAD_MACOS_29_INSTALLER_FILENAME?.trim() ||
  `ArkyvArchicadPlugin-29-${archicadMacos29InstallerVersion}.pkg`;
const archicadMacos29InstallerUrl =
  process.env.NEXT_PUBLIC_ARCHICAD_MACOS_29_INSTALLER_DOWNLOAD_URL?.trim() ||
  `${PLUGIN_DOWNLOAD_BASE_URL}/${archicadMacos29InstallerFilename}`;

const rhinoWindowsInstallerVersion =
  process.env.NEXT_PUBLIC_RHINO_WINDOWS_INSTALLER_VERSION?.trim() ||
  DEFAULT_INSTALLER_VERSION;
const rhinoWindowsInstallerFilename =
  process.env.NEXT_PUBLIC_RHINO_WINDOWS_INSTALLER_FILENAME?.trim() ||
  `ArkyvRhinoPlugin-${rhinoWindowsInstallerVersion}.msi`;
const rhinoWindowsInstallerUrl =
  process.env.NEXT_PUBLIC_RHINO_WINDOWS_INSTALLER_DOWNLOAD_URL?.trim() ||
  `${PLUGIN_DOWNLOAD_BASE_URL}/${rhinoWindowsInstallerFilename}`;

export interface PluginDownload {
  /** Unique identifier for this download */
  id:
    | 'revit-plugin'
    | 'archicad-windows-installer'
    | 'archicad-macos-28-installer'
    | 'archicad-macos-29-installer'
    | 'rhino-windows-installer';
  /** Product host for analytics and UI grouping */
  product: 'revit' | 'archicad' | 'rhino';
  /** Installer operating system */
  platform?: 'windows' | 'macos';
  /** Translation key for the download title */
  titleKey: TranslationKey;
  /** Translation key for the download description */
  descriptionKey: TranslationKey;
  /** Translation key shown when no direct URL is configured */
  unavailableKey?: TranslationKey;
  /** Direct download URL (Vercel Blob) */
  url: string | null;
  /** Filename for the download */
  filename: string;
  /** Plugin release version */
  version: string;
  /** Host application versions supported */
  versions: string[];
}

/**
 * Unified Revit plugin download (supports all versions)
 */
export const PLUGIN_DOWNLOAD: PluginDownload = {
  id: 'revit-plugin',
  product: 'revit',
  titleKey: 'downloads.revitPlugin.title',
  descriptionKey: 'downloads.revitPlugin.description',
  url: pluginDownloadUrl,
  filename: pluginDownloadFilename,
  version: pluginDownloadVersion,
  versions: [...INTEGRATION_SUPPORT.revit.windows],
};

export const ARCHICAD_WINDOWS_INSTALLER_DOWNLOAD: PluginDownload = {
  id: 'archicad-windows-installer',
  product: 'archicad',
  platform: 'windows',
  titleKey: 'downloads.archicadWindowsInstaller.title',
  descriptionKey: 'downloads.archicadWindowsInstaller.description',
  unavailableKey: 'downloads.archicadWindowsInstaller.unavailable',
  url: archicadWindowsInstallerUrl,
  filename: archicadWindowsInstallerFilename,
  version: archicadWindowsInstallerVersion,
  versions: [...INTEGRATION_SUPPORT.archicad.windows],
};

export const ARCHICAD_MACOS_28_INSTALLER_DOWNLOAD: PluginDownload = {
  id: 'archicad-macos-28-installer',
  product: 'archicad',
  platform: 'macos',
  titleKey: 'downloads.archicadMacos28Installer.title',
  descriptionKey: 'downloads.archicadMacos28Installer.description',
  unavailableKey: 'downloads.archicadMacosInstaller.unavailable',
  url: archicadMacos28InstallerUrl,
  filename: archicadMacos28InstallerFilename,
  version: archicadMacos28InstallerVersion,
  versions: [
    '28',
  ] satisfies (typeof INTEGRATION_SUPPORT.archicad.macos)[number][],
};

export const ARCHICAD_MACOS_29_INSTALLER_DOWNLOAD: PluginDownload = {
  id: 'archicad-macos-29-installer',
  product: 'archicad',
  platform: 'macos',
  titleKey: 'downloads.archicadMacos29Installer.title',
  descriptionKey: 'downloads.archicadMacos29Installer.description',
  unavailableKey: 'downloads.archicadMacosInstaller.unavailable',
  url: archicadMacos29InstallerUrl,
  filename: archicadMacos29InstallerFilename,
  version: archicadMacos29InstallerVersion,
  versions: [
    '29',
  ] satisfies (typeof INTEGRATION_SUPPORT.archicad.macos)[number][],
};

export const RHINO_WINDOWS_INSTALLER_DOWNLOAD: PluginDownload = {
  id: 'rhino-windows-installer',
  product: 'rhino',
  platform: 'windows',
  titleKey: 'downloads.rhinoWindowsInstaller.title',
  descriptionKey: 'downloads.rhinoWindowsInstaller.description',
  unavailableKey: 'downloads.rhinoWindowsInstaller.unavailable',
  url: rhinoWindowsInstallerUrl,
  filename: rhinoWindowsInstallerFilename,
  version: rhinoWindowsInstallerVersion,
  versions: [...INTEGRATION_SUPPORT.rhino.windows],
};

export const PLUGIN_DOWNLOADS: PluginDownload[] = [
  PLUGIN_DOWNLOAD,
  ARCHICAD_WINDOWS_INSTALLER_DOWNLOAD,
  ARCHICAD_MACOS_28_INSTALLER_DOWNLOAD,
  ARCHICAD_MACOS_29_INSTALLER_DOWNLOAD,
  RHINO_WINDOWS_INSTALLER_DOWNLOAD,
];
